import { Box, Button, Grid, IconButton, InputAdornment, Skeleton, TextField, Typography } from "@mui/material";
import React, { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
// import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddCircleIcon from "@mui/icons-material/AddCircle";
// import CheckBoxIcon from "@mui/icons-material/CheckBox";
// import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
// import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
// import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteIcon from "@mui/icons-material/Delete";

import "./blacklist.css";
import {
	DataGrid,
	GridColDef,
	GridPagination,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowSelectionModel
} from "@mui/x-data-grid";
import { ROWS_PER_PAGE, VEHICLE_TYPE_E, VEHICLE_TYPES_LIST } from "../../../utils/constants";
import {
	CombinedRowType,
	IGetBlacklistRequestData,
	IGetBlacklistResponseData,
	IUploadBlacklistVehicleExcelDetails,
	IUploadBlacklistVehiclesExcelResponseData,
	IUploadResponse,
	IUploadResponseBlacklistDetails,
	IUploadResponseData
} from "../../../interfaces";
import {
	useDeleteBlacklistVehicleMutation,
	useGetBlacklistQuery,
	useUploadBlacklistVehiclesDataMutation
} from "../../../redux/reducers/dataManagement.reducers";
import ConfirmationDialog from "../../../common/ConfirmationDialog";
import AddBlacklistVehicleDialog from "./AddBlacklistVehicleDialog";
import downloadServices from "../../../redux/services/download.services";
import { LoadingButton } from "@mui/lab";
import TablePagination from "../../../common/TablePagination";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import { useGetSiteConfigQuery } from "../../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../utils/commonUtils";
import NewUploadDataDialog from "../../../common/NewUploadDataDialog";

interface IBlacklistVehicleDetails {
	id: number;
	sr_no: number;
	vehicle_number: string;
	vehicle_type: number;
	reason: string;
}

const BlacklistPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const Blacklist: FC = () => {
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const searchInputRef = useRef<HTMLInputElement>(null);

	const [searchInput, setSearchInput] = useState<string>("");
	const [selectedBlacklistIds, setSelectedBlacklistIds] = useState<number[]>([]);
	// const [enableTableRowSelection, setEnableTableRowSelection] = useState<boolean>(false);
	const [selectedIdToDelete, setSelectedIdToDelete] = useState<number>(-1);
	const [showAddBlacklistVehicleDialog, setShowAddBlacklistVehicleDialog] = useState<boolean>(false);
	const [downloadButtonLoading, setDownloadButtonLoading] = useState<boolean>(false);
	const [filters, setFilters] = useState<IGetBlacklistRequestData>({ page: 1 });
	const [showUploadDataDialog, setShowUploadDataDialog] = useState<boolean>(false);
	const [uploadDataLoading, setUploadDataLoading] = useState<boolean>(false);

	const {
		data: blacklistData,
		isLoading: getBlacklistLoading,
		isFetching: getBlacklistFetching,
		refetch: refetchBlacklistData
	} = useGetBlacklistQuery(filters);

	const [
		deleteBlacklistVehicle,
		{
			isLoading: deleteVehicleLoading,
			isSuccess: deleteVehicleSuccess,
			error: deleteVehicleError,
			reset: resetDeleteBlacklistVehicleState
		}
	] = useDeleteBlacklistVehicleMutation();

	// const [
	// 	uploadBlacklistData,
	// 	{
	// 		isLoading: uploadBlacklistDataLoading,
	// 		isSuccess: uploadBlacklistDataSuccess,
	// 		reset: resetUploadBlacklistDataState
	// 	}
	// ] = useUploadBlacklistDataMutation();

	const [uploadBlacklistVehiclesData] = useUploadBlacklistVehiclesDataMutation();

	const { data: getSiteConfigResponse } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(getSiteConfigResponse?.cloud_sync_enabled ?? true),
		[getSiteConfigResponse]
	);

	// const [vehicleTypeDropdownAnchorElement, setVehicleTypeDropdownAnchorElement] = useState<HTMLElement | null>(null);
	// const openVehicleTypeDropdown = Boolean(vehicleTypeDropdownAnchorElement);

	function handleOpenDeleteConfirmationDialog(blacklistVehicleId: number) {
		setSelectedIdToDelete(blacklistVehicleId);
	}

	function handleCloseDeleteConfirmationDialog() {
		setSelectedIdToDelete(-1);
		refetchBlacklistData();
	}

	function handleOpenAddBlacklistVehicleDialog() {
		setShowAddBlacklistVehicleDialog(true);
	}

	function handleCloseAddBlacklistVehicleDialog() {
		setShowAddBlacklistVehicleDialog(false);
		refetchBlacklistData();
	}

	function handleOpenUploadDataDialog() {
		setShowUploadDataDialog(true);
	}

	function handleCloseUploadDataDialog() {
		setShowUploadDataDialog(false);
		refetchBlacklistData();
	}

	function handleChangeSearchInput(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		setSearchInput(event.target.value);
	}

	function handleClearSearchInput() {
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };
			delete updatedFilters.search;
			return updatedFilters;
		});

		setSearchInput("");

		if (searchInputRef.current) searchInputRef.current.focus();
	}

	function handleSearchBlacklistVehicle(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function handleSelectRows(selectedRows: GridRowSelectionModel) {
		setSelectedBlacklistIds(selectedRows as number[]);
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function handleDeleteBlacklistVehicle() {
		if (selectedIdToDelete >= 0) deleteBlacklistVehicle(selectedIdToDelete);
		else handleCloseDeleteConfirmationDialog();
	}

	function handleDownloadButtonClick() {
		setDownloadButtonLoading(true);

		downloadServices
			.handleDownloadBlacklist(filters)
			.catch((error) => {
				console.error("Download blacklist failed ::", error);
			})
			.finally(() => {
				setDownloadButtonLoading(false);
			});
	}

	function handleUploadBlacklistDetails(
		rowsData: CombinedRowType[],
		_customFlatSelectionEnabled?: boolean,
		isVerified?: boolean | undefined
	): Promise<IUploadResponse> {
		return new Promise((resolve) => {
			setUploadDataLoading(true);

			const uploadData: IUploadBlacklistVehicleExcelDetails[] = [];

			for (const rowDetails of rowsData) {
				if (rowDetails.is_deleted) continue;

				const vehicleTypeDetails = VEHICLE_TYPES_LIST.find(
					(vehicleTypeItem) => vehicleTypeItem.name === rowDetails["Vehicle Type"]
				);

				const uploadDataItem: IUploadBlacklistVehicleExcelDetails = {
					number_plate: rowDetails["Number Plate"],
					vehicle_type: vehicleTypeDetails?.id ?? VEHICLE_TYPE_E.FOUR_WHEELER,
					auth_type: "blacklist",
					comment: rowDetails.Comment
				};

				uploadData.push(uploadDataItem);
			}

			if (uploadData.length <= 0) {
				resolve({
					data: [],
					message: ""
				});
				return;
			}

			uploadBlacklistVehiclesData({
				data: uploadData,
				is_verified: isVerified ?? false
			})
				.unwrap()
				.then((response) => {
					resolve({
						data: [],
						message: response.message
					});

					dispatch(
						showFeedbackNotification({
							message: response.message,
							severity: "success"
						})
					);
				})
				.catch((error) => {
					if (!error.data) {
						resolve({
							data: [],
							message: "Something went wrong"
						});
						return;
					}

					const errorData = error.data as IUploadBlacklistVehiclesExcelResponseData;

					dispatch(
						showFeedbackNotification({
							message: errorData.message,
							severity: "error"
						})
					);

					const filteredErrorData = errorData.data.filter(
						(item) => !!item.database_data && Object.keys(item.database_data).length > 0
					);

					const returnData: IUploadResponseBlacklistDetails[] = [];

					for (const errorDataItem of filteredErrorData) {
						if (!errorDataItem.remarks) continue;

						if (errorDataItem.remarks === "Vehicle already present") {
							const vehicleTypeDetails = VEHICLE_TYPES_LIST.find(
								(vehicleTypeItem) => vehicleTypeItem.id === errorDataItem.database_data.vehicle?.vehicle_type || -1
							);

							returnData.push({
								remarks: errorDataItem.remarks,
								"Number Plate": errorDataItem.database_data.vehicle?.number_plate ?? "",
								"Vehicle Type": vehicleTypeDetails?.name ?? "",
								Comment: errorDataItem.database_data.vehicle?.comment ?? "",
								id: 0,
								sr_no: 0,
								is_deleted: false,
								errorFields:
									errorDataItem.database_data.vehicle && errorDataItem.database_data.vehicle.auth_type === "whitelist"
										? {
												"Number Plate": ["This number plate already exists in whitelist"]
										  }
										: {
												"Number Plate": ["Vehicle already present"]
										  }
							});
						}
					}

					resolve({
						data: returnData as IUploadResponseData[],
						message: errorData.message
					});
				})
				.finally(() => {
					setUploadDataLoading(false);
				});
		});
	}

	// function handleOpenVehicleTypeDropdown(event: MouseEvent<HTMLDivElement>) {
	// 	setVehicleTypeDropdownAnchorElement(event.currentTarget);
	// }

	// function handleCloseVehicleTypeDropdown() {
	// 	setVehicleTypeDropdownAnchorElement(null);
	// }

	// function handleChangeSelectedVehicleType(updatedValue: number) {
	// 	handleCloseVehicleTypeDropdown();
	// 	setFilters((currentFiltersState) => {
	// 		const updatedFilters = { ...currentFiltersState };

	// 		if (updatedValue >= 0) updatedFilters.vehicle_type = updatedValue;
	// 		else delete updatedFilters.vehicle_type;

	// 		updatedFilters.page = 1;

	// 		return updatedFilters;
	// 	});
	// }

	function getBlacklistRowDetails(blacklist: IGetBlacklistResponseData[]): IBlacklistVehicleDetails[] {
		const activePage = filters.page ? filters.page - 1 : 0;

		return blacklist.map((blacklistItem, index) => ({
			id: blacklistItem.id,
			sr_no: activePage * ROWS_PER_PAGE + index + 1,
			vehicle_number: blacklistItem.number_plate,
			vehicle_type: blacklistItem.vehicle_type,
			reason: blacklistItem.comment
		}));
	}

	// function renderVehicleTypeDropdown() {
	// 	return (
	// 		<Box>
	// 			<Box
	// 				component="div"
	// 				className="table-column-header-dropdown-wrapper"
	// 				id="vehicle-type-dropdown"
	// 				aria-controls={openVehicleTypeDropdown ? "vehicle-type-dropdown-menu" : undefined}
	// 				aria-haspopup="true"
	// 				aria-expanded={openVehicleTypeDropdown ? "true" : undefined}
	// 				onClick={handleOpenVehicleTypeDropdown}
	// 			>
	// 				<Typography variant="subtitle2" textTransform="capitalize">
	// 					{typeof filters.vehicle_type === "number"
	// 						? filters.vehicle_type === 0
	// 							? "Four Wheeler"
	// 							: "Two Wheeler"
	// 						: "Vehicle Type"}
	// 				</Typography>
	// 				{openVehicleTypeDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
	// 			</Box>

	// 			<Menu
	// 				id="vehicle-type-dropdown-menu"
	// 				anchorEl={vehicleTypeDropdownAnchorElement}
	// 				open={openVehicleTypeDropdown}
	// 				onClose={handleCloseVehicleTypeDropdown}
	// 				MenuListProps={{ "aria-labelledby": "vehicle-type-dropdown" }}
	// 				sx={{ width: "100%" }}
	// 			>
	// 				<MenuItem onClick={() => handleChangeSelectedVehicleType(-1)} sx={{ minWidth: 130 }}>
	// 					<em>All</em>
	// 				</MenuItem>
	// 				<MenuItem onClick={() => handleChangeSelectedVehicleType(0)}>Four Wheeler</MenuItem>
	// 				<MenuItem onClick={() => handleChangeSelectedVehicleType(1)}>Two Wheeler</MenuItem>
	// 			</Menu>
	// 		</Box>
	// 	);
	// }

	useEffect(() => {
		if (deleteVehicleSuccess) {
			handleCloseDeleteConfirmationDialog();
			resetDeleteBlacklistVehicleState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteVehicleSuccess, resetDeleteBlacklistVehicleState]);

	// useEffect(() => {
	// 	if (uploadBlacklistDataSuccess) {
	// 		handleCloseUploadDataDialog();
	// 		resetUploadBlacklistDataState();
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [resetUploadBlacklistDataState, uploadBlacklistDataSuccess]);

	useEffect(() => {
		if (deleteVehicleError && "data" in deleteVehicleError) {
			dispatch(
				showFeedbackNotification({
					message: deleteVehicleError.data ? String(deleteVehicleError.data) : "Something went wrong",
					severity: "error"
				})
			);

			resetDeleteBlacklistVehicleState();
		}
	}, [deleteVehicleError, dispatch, resetDeleteBlacklistVehicleState]);

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 100,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "vehicle_number",
			headerName: "Vehicle Number",
			flex: 1,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "vehicle_type",
			headerName: "Vehicle Type",
			flex: 1,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			align: "center",
			valueGetter: (params) => (params.value === 0 ? "Four Wheeler" : "Two Wheeler")
		},
		{
			field: "reason",
			headerName: "Reason",
			flex: 2,
			minWidth: 200,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IBlacklistVehicleDetails>) => (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
					{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_BLACKLIST) ? (
						<IconButton size="small" onClick={() => handleOpenDeleteConfirmationDialog(params.row.id)}>
							<DeleteIcon fontSize="inherit" color="error" />
						</IconButton>
					) : null}
				</Box>
			)
		}
	];

	return (
		<Box className="blacklist-screen-wrapper" sx={{ 
			flex: 1, 
			display: "flex", 
			flexDirection: "column", 
			gap: { xs: "0.75rem", sm: "1rem" },
			// Make the page scrollable on mobile instead of just the DataGrid
			overflow: { xs: "auto", sm: "hidden" },
			height: { xs: "100vh", sm: "auto" },
			paddingBottom: { xs: "1rem", sm: 0 }
		}}>
			<Grid container spacing={{ xs: 1, sm: 2 }}>
				<Grid item xs={12} sm={6} md={3}>
					{getBlacklistLoading || getBlacklistFetching ? (
						<Skeleton 
							variant="rounded" 
							animation="wave" 
							sx={{ 
								height: { xs: "100px", sm: "120px" },
								borderRadius: "12px"
							}} 
						/>
					) : (
						<Box className="blacklist-data-card" sx={{
							background: "#ffffff",
							padding: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
							display: "flex",
							alignItems: "center",
							gap: { xs: "0.75rem", sm: "1rem" },
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
							border: "1px solid rgba(0, 0, 0, 0.06)",
							transition: "all 0.3s ease",
							position: "relative",
							overflow: "hidden",
							height: { xs: "100px", sm: "auto" }
						}}>
							<Box className="blacklist-data-card-icon" sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								padding: { xs: "0.5rem", sm: "0.75rem" },
								background: "#ffebee",
								borderRadius: "10px",
								minWidth: { xs: "40px", sm: "48px" },
								height: { xs: "40px", sm: "48px" },
								flexShrink: 0
							}}>
								<DirectionsCarFilledIcon color="error" sx={{ fontSize: { xs: "24px", sm: "28px" } }} />
							</Box>

							<Box className="blacklist-data-card-details" sx={{
								display: "flex",
								flexDirection: "column",
								gap: "0.25rem",
								flex: 1,
								minWidth: 0
							}}>
								<Typography variant="body2" color="#00000080" sx={{ 
									fontSize: { xs: "0.75rem", sm: "0.875rem" },
									lineHeight: { xs: 1.2, sm: 1.4 }
								}}>
									Blacklisted Vehicles
								</Typography>

								<Typography fontWeight={600} sx={{ 
									fontSize: { xs: "1.25rem", sm: "1.5rem" },
									lineHeight: { xs: 1.2, sm: 1.4 }
								}}>
									{Intl.NumberFormat("en-IN").format(blacklistData?.count ?? 0)}
								</Typography>
							</Box>
						</Box>
					)}
				</Grid>
			</Grid>

			<Box className="blacklist-wrapper" sx={{
				background: "#ffffff",
				padding: { xs: "0.75rem", sm: "1rem" },
				borderRadius: "12px",
				flexGrow: { xs: 0, sm: 1 },
				display: "flex",
				flexDirection: "column",
				gap: { xs: "0.75rem", sm: "1rem" },
				boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
				border: "1px solid rgba(0, 0, 0, 0.06)",
				// On mobile, don't constrain height to allow natural flow
				height: { xs: "auto", sm: "auto" },
				minHeight: { xs: "auto", sm: 0 }
			}}>
				<Box className="blacklist-header-wrapper" sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "stretch", sm: "center" },
					justifyContent: "space-between",
					gap: { xs: "0.75rem", sm: "1rem" }
				}}>
					{/* Mobile: Row 1 - Search Input (Full Width) */}
					<Box sx={{ 
						display: { xs: "block", sm: "none" },
						width: "100%"
					}}>
						<Box component="form" noValidate onSubmit={handleSearchBlacklistVehicle}>
							<TextField
								variant="filled"
								placeholder="Search using number plate"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								classes={{ root: "vehicle-number-input-root" }}
								sx={{ 
									width: "100%",
									"& .MuiInputBase-input": {
										fontSize: "0.875rem"
									}
								}}
								autoComplete="off"
								inputRef={searchInputRef}
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root dashboard-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.search ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>
					</Box>

					{/* Mobile: Row 2 - Download & Upload Buttons (Half-Half Widths) */}
					<Box sx={{ 
						display: { xs: "flex", sm: "none" },
						width: "100%",
						gap: "0.5rem"
					}}>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.UPLOAD_BLACKLIST) ? (
							<Button
								variant="outlined"
								color="primary"
								startIcon={<FileUploadIcon />}
								onClick={handleOpenUploadDataDialog}
								sx={{
									fontSize: "0.875rem",
									padding: "6px 12px",
									flex: 1
								}}
							>
								Upload
							</Button>
						) : null}

						<LoadingButton
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={downloadButtonLoading}
							loadingPosition="start"
							sx={{
								fontSize: "0.875rem",
								padding: "6px 12px",
								flex: 1
							}}
						>
							Download
						</LoadingButton>
					</Box>

					{/* Mobile: Row 3 - Add Button (Full Width) */}
					<Box sx={{ 
						display: { xs: "block", sm: "none" },
						width: "100%"
					}}>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADD_BLACKLIST) ? (
							<Button
								variant="contained"
								color="error"
								startIcon={<AddCircleIcon />}
								onClick={handleOpenAddBlacklistVehicleDialog}
								sx={{
									fontSize: "0.875rem",
									padding: "6px 12px",
									width: "100%"
								}}
							>
								Add
							</Button>
						) : null}
					</Box>

					{/* Desktop: Original Layout */}
					<Box className="blacklist-header-actions-wrapper" sx={{
						display: { xs: "none", sm: "flex" },
						alignItems: "center",
						justifyContent: "flex-start",
						gap: "0.75rem",
						flexWrap: "wrap"
					}}>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADD_BLACKLIST) ? (
							<Button
								variant="contained"
								color="error"
								startIcon={<AddCircleIcon />}
								onClick={handleOpenAddBlacklistVehicleDialog}
								sx={{
									fontSize: "1rem",
									padding: "8px 16px"
								}}
							>
								Add
							</Button>
						) : null}
					</Box>

					<Box className="blacklist-header-actions-wrapper" sx={{
						display: { xs: "none", sm: "flex" },
						alignItems: "center",
						justifyContent: "flex-end",
						gap: "0.75rem",
						flexWrap: "wrap"
					}}>
						<Box component="form" noValidate onSubmit={handleSearchBlacklistVehicle}>
							<TextField
								variant="filled"
								placeholder="Search using number plate"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								classes={{ root: "vehicle-number-input-root" }}
								sx={{ 
									width: "300px",
									"& .MuiInputBase-input": {
										fontSize: "1rem"
									}
								}}
								autoComplete="off"
								inputRef={searchInputRef}
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root dashboard-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.search ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>

						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.UPLOAD_BLACKLIST) ? (
							<Button
								variant="outlined"
								color="primary"
								startIcon={<FileUploadIcon />}
								onClick={handleOpenUploadDataDialog}
								sx={{
									fontSize: "1rem",
									padding: "8px 16px"
								}}
							>
								Upload
							</Button>
						) : null}

						<LoadingButton
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={downloadButtonLoading}
							loadingPosition="start"
							sx={{
								fontSize: "1rem",
								padding: "8px 16px"
							}}
						>
							Download
						</LoadingButton>
					</Box>
				</Box>

				<Box className="blacklist-table-wrapper" sx={{
					// On mobile, use auto height instead of flex-grow to allow natural flow
					flexGrow: { xs: 0, sm: 1 },
					background: "#f8f9fa",
					// Remove height constraint on mobile to allow natural flow
					height: { xs: "auto", sm: 0 },
					display: "flex",
					flexDirection: "column",
					// Set minimum height for mobile to ensure table is visible
					minHeight: { xs: "400px", sm: "auto" }
				}}>
					<DataGrid
						columns={columnsData}
						rows={getBlacklistRowDetails(blacklistData?.results ?? [])}
						getRowId={(item) => item.id}
						rowSelectionModel={selectedBlacklistIds}
						onRowSelectionModelChange={handleSelectRows}
						loading={getBlacklistLoading || getBlacklistFetching}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={65}
						paginationMode="server"
						pageSizeOptions={[ROWS_PER_PAGE]}
						onPaginationModelChange={handlePageChange}
						rowCount={blacklistData?.count ?? 0}
						paginationModel={{ page: filters.page ? filters.page - 1 : 0, pageSize: ROWS_PER_PAGE }}
						slots={{ pagination: BlacklistPagination }}
						slotProps={{ footer: { sx: { justifyContent: "center" } } }}
						hideFooter={(blacklistData?.count ?? 0) <= ROWS_PER_PAGE}
						classes={{
							columnSeparator: "blacklist-table-column-separator",
							columnHeader: "blacklist-table-column-headers",
							cell: "blacklist-table-cell",
							root: "blacklist-table-root"
						}}
						sx={{
							"& .MuiDataGrid-cell": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							},
							"& .MuiDataGrid-columnHeader": {
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							},
							// On mobile, set a fixed height for the DataGrid to prevent overflow
							height: { xs: "400px", sm: "auto" }
						}}
					/>
				</Box>
			</Box>

			<ConfirmationDialog
				open={selectedIdToDelete >= 0}
				title={"Delete Vehicle"}
				heading={"Are you sure you want to remove this vehicle from blacklist?"}
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteBlacklistVehicle}
				color="error"
				confirmButtonLoading={deleteVehicleLoading}
			/>

			<AddBlacklistVehicleDialog open={showAddBlacklistVehicleDialog} onClose={handleCloseAddBlacklistVehicleDialog} />

			{/* <UploadDataDialog
				open={showUploadDataDialog}
				onClose={handleCloseUploadDataDialog}
				onUploadButtonClick={handleUploadBlacklistDetails}
				sampleFileUrl={"/analytics/blacklist_vehicles/download_template"}
				sampleFileName={"BlacklistSample.xlsx"}
				uploadButtonLoading={uploadBlacklistDataLoading}
			/> */}

			<NewUploadDataDialog
				open={showUploadDataDialog}
				sampleFileUrl="/analytics/blacklist_vehicles/download_template"
				onClose={handleCloseUploadDataDialog}
				onUploadButtonClick={handleUploadBlacklistDetails}
				uploadButtonLoading={uploadDataLoading}
				disableCustomFlatNumber
			/>
		</Box>
	);
};

export default Blacklist;
